import { createFileRoute, Link } from "@tanstack/react-router";
import { HelpCircle, MessageSquare, ArrowRight, ArrowUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQ_GROUPS, ALL_FAQS } from "@/lib/faq-data";
import { buildFaqPageJsonLd, validateFaqGroups } from "@/lib/faq-jsonld";
import { FaqSearchInput, useFaqFilter } from "@/components/sections/FaqSearch";
import { trackEvent } from "@/lib/analytics";

const SITE = "https://kentro.com.br";
const URL = `${SITE}/perguntas-frequentes`;
const TITLE = "Perguntas frequentes — Kentro Sistemas";
const DESC =
  "Tire dúvidas sobre a Kentro: plataforma de atendimento omnichannel com CRM e IA, API Oficial do WhatsApp, integrações, segmentos, LGPD, IA, vendas e implantação.";

// Valida a estrutura das FAQs em dev — quebra cedo se um grupo novo estiver malformado.
if (import.meta.env.DEV) {
  const issues = validateFaqGroups(FAQ_GROUPS);
  const errors = issues.filter((i) => i.level === "error");
  if (errors.length > 0) {
    console.error("[FAQ JSON-LD] estrutura inválida:", errors);
  }
  const warnings = issues.filter((i) => i.level === "warning");
  if (warnings.length > 0) {
    console.warn("[FAQ JSON-LD] avisos:", warnings);
  }
}

export const Route = createFileRoute("/perguntas-frequentes")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "pt-BR", href: URL },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildFaqPageJsonLd(FAQ_GROUPS, {
            pageUrl: URL,
            siteUrl: SITE,
            pageTitle: TITLE,
            pageDescription: DESC,
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Perguntas frequentes", item: URL },
          ],
        }),
      },
    ],
  }),
  component: FaqPage,
});

function FaqPage() {
  const { query, setQuery, filtered, total, isFiltering } = useFaqFilter(FAQ_GROUPS);
  const visibleGroups = filtered;
  const [activeGroup, setActiveGroup] = useState<string>(visibleGroups[0]?.id ?? "");

  useEffect(() => {
    const ids = visibleGroups.map((g) => g.id);
    if (ids.length === 0) return;
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveGroup(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -60% 0px", threshold: 0 },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [visibleGroups]);

  // Debounce de busca — dispara uma vez por consulta "estável".
  const lastSearchRef = useRef<string>("");
  useEffect(() => {
    const q = query.trim();
    if (!q || q.length < 2) return;
    const handle = window.setTimeout(() => {
      if (lastSearchRef.current === q) return;
      lastSearchRef.current = q;
      trackEvent("faq_search", {
        query: q.slice(0, 120),
        query_length: q.length,
        results_count: total,
        has_results: total > 0,
      });
    }, 800);
    return () => window.clearTimeout(handle);
  }, [query, total]);

  // Tempo na seção — dispara no unmount, no beforeunload e ao esconder a aba.
  useEffect(() => {
    const start = Date.now();
    let sent = false;
    const flush = (reason: string) => {
      if (sent) return;
      sent = true;
      trackEvent("faq_time_on_section", {
        duration_seconds: Math.round((Date.now() - start) / 1000),
        reason,
      });
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") flush("visibility_hidden");
    };
    const onUnload = () => flush("pagehide");
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onUnload);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onUnload);
      flush("unmount");
    };
  }, []);

  // Detecta acordeões abertos comparando com o valor anterior.
  const openStateRef = useRef<Record<string, string[]>>({});
  const handleAccordionChange = (groupId: string, value: string[]) => {
    const prev = openStateRef.current[groupId] ?? [];
    const opened = value.filter((v) => !prev.includes(v));
    openStateRef.current[groupId] = value;
    for (const key of opened) {
      const idx = Number(key.split("-").pop());
      const group = FAQ_GROUPS.find((g) => g.id === groupId);
      const item = group?.items[idx];
      if (item) {
        trackEvent("faq_question_open", {
          question_id: item.id,
          group_id: groupId,
          question: item.q.slice(0, 120),
        });
      }
    }
  };

  const handleGroupNav = (groupId: string, location: "hero_summary" | "sticky_nav") => {
    trackEvent("faq_group_nav_click", { group_id: groupId, location });
  };

  const scrollToTop = () => {
    trackEvent("faq_back_to_top_click", {});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <>
      {/* Hero */}
      <section className="gradient-hero relative overflow-hidden pt-24 pb-16">
        <div className="circuit-pattern absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">Perguntas frequentes</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/40 px-3 py-1 text-xs text-muted-foreground">
            <HelpCircle className="size-3.5" /> FAQ · Kentro Sistemas
          </div>
          <h1
            className="mt-4 text-4xl font-bold tracking-tight md:text-5xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Perguntas <span className="gradient-text">frequentes</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
            Respostas diretas sobre a plataforma Kentro — atendimento omnichannel, CRM, IA, API
            Oficial do WhatsApp, integrações, segmentos atendidos, LGPD e implantação.
          </p>

          {/* Busca */}
          <FaqSearchInput
            className="mt-8 max-w-xl"
            query={query}
            onChange={setQuery}
            total={total}
            isFiltering={isFiltering}
            suggestionsSource={FAQ_GROUPS}
          />


          {/* Sumário (hero) */}
          <nav aria-label="Sumário de categorias" className="mt-4">
            <ul className="flex flex-wrap gap-2">
              {FAQ_GROUPS.map((g) => (
                <li key={g.id}>
                  <a
                    href={`#${g.id}`}
                    onClick={() => handleGroupNav(g.id, "hero_summary")}
                    className="inline-flex items-center rounded-full border border-white/10 bg-card/40 px-3 py-1.5 text-xs font-medium text-muted-foreground outline-none transition hover:bg-card/70 hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    {g.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </section>

      {/* Navegação sticky entre grupos */}
      <nav
        aria-label="Navegação entre grupos de FAQ"
        className="sticky top-0 z-30 border-b border-white/10 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <ul className="scrollbar-none flex gap-2 overflow-x-auto py-3">
            {visibleGroups.map((g) => {
              const isActive = activeGroup === g.id;
              return (
                <li key={g.id} className="shrink-0">
                  <a
                    href={`#${g.id}`}
                    onClick={() => handleGroupNav(g.id, "sticky_nav")}
                    aria-current={isActive ? "true" : undefined}
                    className={
                      "inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-medium outline-none transition focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background " +
                      (isActive
                        ? "border-primary/60 bg-primary/15 text-foreground"
                        : "border-white/10 bg-card/40 text-muted-foreground hover:bg-card/70 hover:text-foreground")
                    }
                  >
                    {g.title}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>


      {/* Conteúdo */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          {/*
            Bloco semântico sempre presente no HTML para crawlers e LLMs
            (Google, ChatGPT, Perplexity). Visualmente oculto. O accordion
            abaixo é puramente visual.
          */}
          <div className="sr-only" itemScope itemType="https://schema.org/FAQPage">
            {ALL_FAQS.map((f) => (
              <article
                key={`seo-${f.id}`}
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <h3 itemProp="name">{f.q}</h3>
                <div
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <p itemProp="text">{f.a}</p>
                </div>
              </article>
            ))}
          </div>

          {visibleGroups.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-card/40 p-8 text-center">
              <p className="text-sm text-muted-foreground">
                Nenhuma pergunta encontrada para <span className="text-foreground font-medium">"{query}"</span>.
              </p>
            </div>
          ) : (
            visibleGroups.map((group) => (
              <div key={group.id} id={group.id} className="mb-12 scroll-mt-24">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2
                      className="text-2xl font-bold tracking-tight md:text-3xl"
                      style={{ fontFamily: "var(--font-display)" }}
                    >
                      {group.title}
                    </h2>
                    {group.description && (
                      <p className="mt-2 text-sm text-muted-foreground">{group.description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={scrollToTop}
                    className="mt-1 inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-card/40 px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-card/70 hover:text-foreground"
                    aria-label="Voltar ao topo"
                  >
                    <ArrowUp className="size-3.5" /> Topo
                  </button>
                </div>

                <Accordion
                  type="multiple"
                  className="mt-6"
                  defaultValue={isFiltering ? group.items.map((_, i) => `${group.id}-${i}`) : undefined}
                  onValueChange={(v) => handleAccordionChange(group.id, v)}
                >
                  {group.items.map((f, i) => (
                    <AccordionItem
                      key={f.id}
                      value={`${group.id}-${i}`}
                      id={f.id}
                      className="scroll-mt-24 border-white/10"
                    >
                      <AccordionTrigger className="text-left text-base font-semibold">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                        <p>{f.a}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))
          )}

          {/* CTA */}
          <div className="mt-16 rounded-2xl border border-white/10 bg-card/40 p-8 text-center">
            <MessageSquare className="mx-auto size-8 text-primary" />
            <h2
              className="mt-3 text-2xl font-bold"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Não encontrou sua dúvida?
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Nosso time comercial responde rapidamente e pode mostrar a Kentro em ação para o
              seu cenário.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/contato"
                className="gradient-primary inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold text-white shadow-glow hover:opacity-95"
              >
                Falar com a Kentro <ArrowRight className="size-4" />
              </Link>
              <Link
                to="/omnichannel"
                className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold hover:bg-white/10"
              >
                Conhecer a plataforma
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
